import { Injectable } from '@angular/core';
import { StorageEngine } from './storage-engine.interface';
import { BrowserStorageService } from './browser-storage.service';
import { ServerStorageService } from './server-storage.service';
import { PlatformService } from '../platform.service';

@Injectable({ providedIn: 'root' })
export class StorageService implements StorageEngine {
  private engine: StorageEngine;

  constructor(
    private platformService: PlatformService,
    browserStorage: BrowserStorageService,
    serverStorage: ServerStorageService,
  ) {
    this.engine = this.platformService.isBrowser() ? browserStorage : serverStorage;
  }

  getItem(key: string): string | null {
    return this.engine.getItem(key);
  }

  setItem(key: string, value: string): void {
    this.engine.setItem(key, value);
  }

  removeItem(key: string): void {
    this.engine.removeItem(key);
  }

  clear(): void {
    this.engine.clear();
  }

  /**
   * Set user's notification decision
   * @param userId - User ID to associate the decision with
   * @param decision - User's decision about notifications ('allowed' | 'denied')
   */
  setUserNotificationDecision(userId: string, decision: 'allowed' | 'denied'): void {
    const key = 'notificationDecision';
    const existingData = this.getItem(key);
    let notificationDecisions: Record<string, 'allowed' | 'denied'> = {};

    if (existingData) {
      try {
        notificationDecisions = JSON.parse(existingData);
      } catch (error) {
        console.warn('Error parsing existing notification decisions:', error);
        notificationDecisions = {};
      }
    }

    notificationDecisions[userId] = decision;
    this.setItem(key, JSON.stringify(notificationDecisions));
  }

  /**
   * Get user's notification decision
   * @param userId - User ID to get the decision for
   * @returns User's notification decision or null if not set
   */
  getUserNotificationDecision(userId: string): 'allowed' | 'denied' | null {
    const key = 'notificationDecision';
    const data = this.getItem(key);

    if (!data) {
      return null;
    }

    try {
      const notificationDecisions: Record<string, 'allowed' | 'denied'> = JSON.parse(data);
      return notificationDecisions[userId] || null;
    } catch (error) {
      console.warn('Error parsing notification decisions:', error);
      return null;
    }
  }

  /**
   * Remove user's notification decision
   * @param userId - User ID to remove the decision for
   */
  removeUserNotificationDecision(userId: string): void {
    const key = 'notificationDecision';
    const data = this.getItem(key);

    if (!data) {
      return;
    }

    try {
      const notificationDecisions: Record<string, 'allowed' | 'denied'> = JSON.parse(data);
      delete notificationDecisions[userId];

      if (Object.keys(notificationDecisions).length === 0) {
        this.removeItem(key);
      } else {
        this.setItem(key, JSON.stringify(notificationDecisions));
      }
    } catch (error) {
      console.warn('Error parsing notification decisions for removal:', error);
    }
  }
}

